import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton as MUILoadingButton } from '@mui/lab';
import type { ButtonProps as MUIButtonProps } from '@mui/material';
import { Button as MUIButton } from '@mui/material';
import { forwardRef, useState } from 'react';

import { getIconForButton, getVariantColorForBtn } from './computeButtonAttributes';
import { BtnIconType, BtnTypeType } from './types';

/**
 * Local Props for Button shared component
 */
type LocalButtonProps = {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id: string;

  /**
   * @prop {string} displayLabel
   */
  displayLabel: string | React.ReactNode;

  /**
   * @prop {boolean} loadingButton - if true the button will be displayed with the Save icon and the displayLabel, also disabled
   */
  waitButton?: boolean;

  /**
   * @prop {boolean} countdownButton - if true the button will be displayed with the countdown timer
   */
  countdownButton?: boolean;

  /**
   * @prop {boolean} countdownStart - if true the countdown timer will start
   */
  countdownStart?: boolean;

  /**
   * @prop {number} countdownTime - the time in seconds for the countdown
   * @default
   */
  countdownTime?: number;

  /**
   * @prop {'primary' | 'secondary' | 'destructive' | 'text' |  'icon'} type - type of button (color of the button) - default normal
   */
  btnType?: BtnTypeType;
};

/**
 * Override props for MUI Button - this allow the app's to control the behavior of the Button as needed
 * When overriding a prop, we should also omit the prop from the MuiButtonProps selection
 * to ensure that the prop def when viewed only shows our override
 */
type MUIOverriddenButtonProps = {
  /**
   * @prop {'add' | 'cancel' | 'checkCircle' | 'check' | 'calendar' | 'folder' | 'progress' | 'radioBtnUnchecked' | 'remove' | 'warning'| 'minus'| 'back' | React.ReactNode} startIcon - an icon in front of the displayLabel
   */
  startIcon?: BtnIconType;

  /**
   * @prop {'add' | 'cancel' | 'checkCircle' | 'check' | 'folder' | 'progress' | 'radioBtnUnchecked' | 'remove' | 'warning' | React.ReactNode} rndIcon - an icon in front of the displayLabel
   */
  endIcon?: BtnIconType;

  /**
   * @prop {callback} onClick - click handler
   * @param {string} id - id of the button
   * @param {React.MouseEvent<HTMLButtonElement>} e - the event
   */
  onClickHandler?: (id: string, e?: React.MouseEvent<HTMLButtonElement>) => void;
};

export type ButtonProps = LocalButtonProps &
  MUIOverriddenButtonProps &
  Omit<MUIButtonProps, 'startIcon' | 'endIcon' | 'onClick'>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    id,
    displayLabel,
    btnType,
    waitButton,
    countdownButton,
    countdownStart,
    countdownTime = 5,

    // MUI Button props
    startIcon,
    endIcon,
    onClickHandler,
    variant,
    color,
    size,
    ...rest
  } = props;

  const onClickLocalHandler = (id: string, e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClickHandler) {
      onClickHandler(id, e);
    }
  };

  // get the variant and color for the button
  const variantAndColor = getVariantColorForBtn(variant, color, btnType);

  // get the start and end icons
  const leftIcon = getIconForButton(startIcon);
  const rightIcon = getIconForButton(endIcon);

  // countdown timer
  const [countdown, setCountdown] = useState(countdownTime);
  let countdownTimer: ReturnType<typeof setTimeout> | null;
  if (countdownButton && countdownStart && countdownTime) {
    countdownTimer = setTimeout(() => {
      if (countdown > 1) {
        setCountdown(countdown - 1);
      } else {
        if (countdownTimer) {
          clearTimeout(countdownTimer);
        }
        countdownTimer = null;

        // reset the countdown
        setCountdown(countdownTime);

        onClickHandler && onClickHandler(id);
      }
    }, 1000);
  }

  // button label
  const buttonLabel = countdownButton ? `${countdown}...  ${displayLabel}` : displayLabel;

  // see if the loading button should be displayed
  if (waitButton) {
    return (
      <MUILoadingButton
        {...rest}
        ref={ref}
        id={`button-${id}`}
        sx={{ whiteSpace: 'nowrap', ...rest.sx }}
        data-testid={`button-${id}`}
        loading
        loadingPosition="start"
        startIcon={<SaveIcon />}
        variant={variantAndColor.variant}
        color={variantAndColor.color}
        size={size}
        onClick={(e) => onClickLocalHandler(id, e)}
      >
        {displayLabel}
      </MUILoadingButton>
    );
  }

  return (
    <MUIButton
      {...rest}
      ref={ref}
      sx={{ whiteSpace: 'nowrap', ...rest.sx }}
      data-testid={`button-${id}`}
      variant={variantAndColor.variant}
      color={variantAndColor.color}
      size={size}
      startIcon={leftIcon}
      endIcon={rightIcon}
      onClick={(e) => onClickLocalHandler(id, e)}
    >
      {buttonLabel}
    </MUIButton>
  );
});
